/**
 * Design board attachable file presentation and read-probe helpers.
 */

import { resolveCanonicalPmProjectFileStoragePath } from "./projectDesignBoardFileReadPathModel";
import { projectFileAllowsLegacyTokenizedUrlCompat } from "./projectFileSecureStorageModel";
import {
  enrichDesignBoardFileRecord,
  probeDesignBoardFileReadableForPicker,
} from "./designBoardProjectFileReadResolver";

function safeStr(value) {
  return String(value ?? "").trim();
}

const probeCache = new Map();

export function clearDesignBoardAttachableFileProbeCache() {
  probeCache.clear();
}

export function designBoardAttachableFileLabel(file = {}) {
  return safeStr(file?.name || file?.displayName || file?.title) || safeStr(file?.id) || "File";
}

export function designBoardAttachableFileSourceLabel(file = {}) {
  const category = safeStr(file?.category);
  const sub = safeStr(file?.subcategory);
  const library = safeStr(file?.libraryType);
  const domain = safeStr(file?.projectFileDomain || file?.storageAuthDomain);
  const parts = [];
  if (library) parts.push(library.replace(/_/g, " "));
  if (domain && domain !== library) parts.push(domain.replace(/_/g, " "));
  if (category && category !== "uncategorized") parts.push(category.replace(/_/g, " "));
  if (sub && sub !== "none") parts.push(sub.replace(/_/g, " "));
  return parts.length ? parts.join(" · ") : "Project file";
}

export function designBoardAttachableFileDateLabel(file = {}) {
  const raw = file?.uploadedAt || file?.createdAt || file?.updatedAt;
  if (!raw) return "";
  try {
    const d = typeof raw?.toDate === "function" ? raw.toDate() : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export function designBoardAttachableFileHasBinding(file = {}) {
  if (resolveCanonicalPmProjectFileStoragePath(file)) return true;
  if (projectFileAllowsLegacyTokenizedUrlCompat(file) && safeStr(file?.url)) return true;
  return false;
}

export function getDesignBoardAttachableFileProbe(fileId) {
  return probeCache.get(safeStr(fileId)) || null;
}

export function setDesignBoardAttachableFileProbe(fileId, result) {
  const fid = safeStr(fileId);
  if (!fid) return;
  probeCache.set(fid, result);
}

/**
 * Probe signed read URL once per file (picker thumbnails). Does not broaden permissions.
 */
export async function probeDesignBoardAttachableFileReadable(
  { projectId = "", file = {} } = {},
  deps = {}
) {
  const enriched = enrichDesignBoardFileRecord(file, { projectId });
  const fid = safeStr(enriched?.id || enriched?.fileId);
  const pid = safeStr(projectId || enriched?.projectId);
  if (!fid || !pid) {
    return { ok: false, status: "invalid", error: "Missing file reference." };
  }
  const cached = getDesignBoardAttachableFileProbe(fid);
  if (cached) return cached;

  if (!designBoardAttachableFileHasBinding(enriched)) {
    const result = { ok: false, status: "unavailable", error: "No storage binding on file record." };
    setDesignBoardAttachableFileProbe(fid, result);
    return result;
  }

  const result = await probeDesignBoardFileReadableForPicker({ projectId: pid, file: enriched }, deps);
  setDesignBoardAttachableFileProbe(fid, result);
  return result;
}

export function designBoardAttachableFileIsBlockedForAttach(probeResult) {
  if (!probeResult) return false;
  return probeResult.status === "missing_object";
}
